import { createSelector } from 'reselect';
import {
    SELECTED_DATE,
    SELECTED_YEAR_GROUP,
    SELECTED_TEACHER,
    SELECTED_CLASS,
    SELECTED_GROUP,
    SELECTED_STUDENT,
    SELECTED_REPORT,
    ALL_STUDENTS,
    ALL_GROUPS,
    ALL_CLASSES,
    ALL_YEARS,
    ALL_TEACHERS,
    ACTIVITY_MASTERY
} from '../../filter-constants';
import {
    REPORT_LEVEL_SINGLE_CLASS,
    REPORT_LEVEL_GROUP,
    REPORT_LEVEL_STUDENT,
    REPORT_LEVEL_ALL_CLASSES,
    REPORT_LEVEL_ALL_YEARS,
    REPORT_LEVEL_YEAR_GROUP_TEACHER
} from '../../../sub-heading/export-report/export-report-actions';

/**
 * Redux selectors for active filters
 */

const selectActiveFilters = (state) => state.activeFilters;
const selectIsAdmin = (state) => state.isAdmin;

const selectFilter = (filterName) =>
    createSelector(
        [selectActiveFilters],
        (activeFilters) => activeFilters[filterName]
    );

const selectItemId = (filterName, defaultOption) =>
    createSelector([selectFilter(filterName)], (filter) =>
        filter && filter.id != null ? filter.id : defaultOption
    );

const selectItemName = (filterName) =>
    createSelector(
        [selectFilter(filterName)],
        (filter) => filter && filter.name
    );

const selectDate = selectFilter(SELECTED_DATE);
const selectYearGroup = selectFilter(SELECTED_YEAR_GROUP);
const selectTeacher = selectFilter(SELECTED_TEACHER);
const selectClass = selectFilter(SELECTED_CLASS);
const selectGroup = selectFilter(SELECTED_GROUP);
const selectStudent = selectFilter(SELECTED_STUDENT);
const selectReport = selectFilter(SELECTED_REPORT);

const selectYearGroupId = selectItemId(SELECTED_YEAR_GROUP, ALL_YEARS);
const selectTeacherId = selectItemId(SELECTED_TEACHER, ALL_TEACHERS);
const selectClassId = selectItemId(SELECTED_CLASS, ALL_CLASSES);
const selectGroupId = selectItemId(SELECTED_GROUP, ALL_GROUPS);
const selectStudentId = selectItemId(SELECTED_STUDENT, ALL_STUDENTS);
const selectReportId = selectItemId(SELECTED_REPORT, ACTIVITY_MASTERY);

const selectYearGroupIdForApi = createSelector(
    [selectYearGroupId],
    (yearGroupId) => (yearGroupId !== ALL_YEARS ? yearGroupId : 'all')
);
const selectTeacherIdForApi = createSelector([selectTeacherId], (teacherId) =>
    teacherId !== ALL_TEACHERS ? teacherId : 0
);

const selectReportLevel = createSelector(
    [
        selectStudentId,
        selectGroupId,
        selectClassId,
        selectTeacherId,
        selectYearGroupId,
        selectIsAdmin
    ],
    (
        selectedStudentId,
        selectedGroupId,
        selectedClassId,
        selectedTeacherId,
        selectedYearGroupId,
        isAdmin
    ) => {
        if (selectedStudentId !== ALL_STUDENTS) {
            return REPORT_LEVEL_STUDENT;
        } else if (selectedGroupId !== ALL_GROUPS) {
            return REPORT_LEVEL_GROUP;
        } else if (selectedClassId !== ALL_CLASSES) {
            return REPORT_LEVEL_SINGLE_CLASS;
        } else if (
            selectedTeacherId !== ALL_TEACHERS ||
            selectedYearGroupId !== ALL_YEARS
        ) {
            return REPORT_LEVEL_YEAR_GROUP_TEACHER;
        } else if (isAdmin) {
            return REPORT_LEVEL_ALL_YEARS;
        }
        return REPORT_LEVEL_ALL_CLASSES;
    }
);

const selectExportReportHeading = createSelector(
    [selectReportLevel, selectYearGroup, selectTeacher],
    (reportLevel, yearGroup, teacher) => {
        const i18n = window.i18n;
        const yearGroupName =
            yearGroup.name === ALL_YEARS
                ? i18n._('Report Filters - All years')
                : yearGroup.name;
        const teacherName =
            teacher.name === ALL_TEACHERS
                ? i18n._('Report Filters - All teachers')
                : i18n._(teacher.name);
        const allClasses = i18n._('Export - All classes');

        switch (reportLevel) {
            case REPORT_LEVEL_ALL_YEARS:
            case REPORT_LEVEL_YEAR_GROUP_TEACHER:
                return `${yearGroupName} | ${teacherName}`;
            case REPORT_LEVEL_ALL_CLASSES:
                return allClasses;
            default:
                return '';
        }
    }
);

export default {
    selectActiveFilters,
    selectFilter,
    selectItemId,
    selectItemName,
    selectDate,
    selectYearGroup,
    selectTeacher,
    selectClass,
    selectGroup,
    selectStudent,
    selectReport,
    selectYearGroupId,
    selectTeacherId,
    selectClassId,
    selectGroupId,
    selectStudentId,
    selectReportId,
    selectYearGroupIdForApi,
    selectTeacherIdForApi,
    selectReportLevel,
    selectExportReportHeading
};



// WEBPACK FOOTER //
// ./components/report-filters/redux/active-filters/active-filters-selectors.js